/**
 * Pup Spice Custom JavaScript for Shopify Refresh Theme
 * 
 * This script provides interactive enhancements and premium functionality
 * for the Pup Spice website.
 * 
 * Installation: Upload to Assets folder in Shopify theme editor,
 * then add to theme.liquid before </body>: {{ 'pup-spice-custom.js' | asset_url | script_tag }}
 */

(function() {
  'use strict';

  // ========================================
  // SMOOTH SCROLL FOR ANCHOR LINKS
  // ========================================
  
  function initSmoothScroll() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    
    anchorLinks.forEach(link => {
      link.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        
        // Skip if it's just "#" or empty
        if (href === '#' || href === '') return;
        
        const target = document.querySelector(href);
        
        if (target) {
          e.preventDefault();
          const headerOffset = 100;
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  // ========================================
  // TESTIMONIAL SLIDER
  // ========================================
  
  function initTestimonialSlider() {
    const slider = document.querySelector('.testimonial-slider');
    if (!slider) return;
    
    const slides = slider.querySelectorAll('.testimonial-card');
    const prevBtn = slider.querySelector('.testimonial-prev');
    const nextBtn = slider.querySelector('.testimonial-next');
    const dotsContainer = slider.querySelector('.testimonial-dots');
    
    let currentSlide = 0;
    const totalSlides = slides.length;
    
    // Create dots
    if (dotsContainer) {
      for (let i = 0; i < totalSlides; i++) {
        const dot = document.createElement('button');
        dot.classList.add('testimonial-dot');
        dot.setAttribute('aria-label', `Go to testimonial ${i + 1}`);
        if (i === 0) dot.classList.add('active');
        dot.addEventListener('click', () => goToSlide(i));
        dotsContainer.appendChild(dot);
      }
    }
    
    function showSlide(index) {
      slides.forEach((slide, i) => {
        slide.style.display = i === index ? 'block' : 'none';
        slide.classList.toggle('active', i === index);
      });
      
      // Update dots
      const dots = dotsContainer?.querySelectorAll('.testimonial-dot');
      dots?.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
    }
    
    function goToSlide(index) {
      currentSlide = index;
      showSlide(currentSlide);
    }
    
    function nextSlide() {
      currentSlide = (currentSlide + 1) % totalSlides;
      showSlide(currentSlide);
    }
    
    function prevSlide() {
      currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
      showSlide(currentSlide);
    }
    
    // Event listeners
    nextBtn?.addEventListener('click', nextSlide);
    prevBtn?.addEventListener('click', prevSlide);
    
    // Auto-advance every 6 seconds
    let autoAdvance = setInterval(nextSlide, 6000);
    
    // Pause on hover
    slider.addEventListener('mouseenter', () => {
      clearInterval(autoAdvance);
    });
    
    slider.addEventListener('mouseleave', () => {
      autoAdvance = setInterval(nextSlide, 6000);
    });
    
    // Initialize
    showSlide(0);
  }

  // ========================================
  // SCROLL ANIMATIONS
  // ========================================
  
  function initScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    // Observe elements with data-animate attribute
    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach(el => {
      observer.observe(el);
    });
  }

  // ========================================
  // PRODUCT VARIANT COLOR ACCENT
  // ========================================
  
  function initProductVariantAccents() {
    const productCards = document.querySelectorAll('.product-card');
    
    productCards.forEach(card => {
      const productTitle = card.querySelector('.product-card__title')?.textContent.toLowerCase();
      
      if (productTitle) {
        if (productTitle.includes('chicken')) {
          card.classList.add('product-card--chicken');
        } else if (productTitle.includes('beef')) {
          card.classList.add('product-card--beef');
        } else if (productTitle.includes('salmon')) {
          card.classList.add('product-card--salmon');
        }
      }
    });
  }

  // ========================================
  // QUANTITY SELECTOR ENHANCEMENT
  // ========================================
  
  function initQuantitySelector() {
    const quantityInputs = document.querySelectorAll('input[type="number"][name="quantity"]');
    
    quantityInputs.forEach(input => {
      const wrapper = input.closest('.quantity-selector') || input.parentElement;
      
      // Create custom buttons if they don't exist
      if (!wrapper.querySelector('.qty-btn-minus')) {
        const minusBtn = document.createElement('button');
        minusBtn.type = 'button';
        minusBtn.classList.add('qty-btn', 'qty-btn-minus');
        minusBtn.innerHTML = '−';
        minusBtn.setAttribute('aria-label', 'Decrease quantity');
        
        const plusBtn = document.createElement('button');
        plusBtn.type = 'button';
        plusBtn.classList.add('qty-btn', 'qty-btn-plus');
        plusBtn.innerHTML = '+';
        plusBtn.setAttribute('aria-label', 'Increase quantity');
        
        wrapper.insertBefore(minusBtn, input);
        wrapper.appendChild(plusBtn);
        
        // Event listeners
        minusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const minValue = parseInt(input.min) || 1;
          if (currentValue > minValue) {
            input.value = currentValue - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        
        plusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const maxValue = parseInt(input.max) || 999;
          if (currentValue < maxValue) {
            input.value = currentValue + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });
  }

  // ========================================
  // ADD TO CART FEEDBACK
  // ========================================
  
  function initAddToCartFeedback() {
    document.addEventListener('submit', function(e) {
      const form = e.target;
      
      // Check if it's an add to cart form
      if (form.querySelector('[name="add"]') || form.action?.includes('/cart/add')) {
        const submitBtn = form.querySelector('[type="submit"]');
        
        if (submitBtn) {
          const originalText = submitBtn.textContent;
          submitBtn.textContent = 'Adding...';
          submitBtn.disabled = true;
          
          // Reset after 2 seconds (Shopify will handle the actual cart update)
          setTimeout(() => {
            submitBtn.textContent = '✓ Added!';
            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
            }, 1500);
          }, 500);
        }
      }
    });
  }

  // ========================================
  // STICKY HEADER ENHANCEMENT
  // ========================================
  
  function initStickyHeader() {
    const header = document.querySelector('.shopify-section-header');
    if (!header) return;
    
    let lastScrollTop = 0;
    const headerHeight = header.offsetHeight;
    
    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (scrollTop > headerHeight) {
        header.classList.add('is-scrolled');
        
        // Hide on scroll down, show on scroll up
        if (scrollTop > lastScrollTop && scrollTop > headerHeight * 2) {
          header.style.transform = 'translateY(-100%)';
        } else {
          header.style.transform = 'translateY(0)';
        }
      } else {
        header.classList.remove('is-scrolled');
        header.style.transform = 'translateY(0)';
      }
      
      lastScrollTop = scrollTop;
    });
  }

  // ========================================
  // IMAGE LAZY LOADING ENHANCEMENT
  // ========================================
  
  function initLazyLoading() {
    const lazyImages = document.querySelectorAll('img[data-src]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        });
      });
      
      lazyImages.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for older browsers
      lazyImages.forEach(img => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    }
  }

  // ========================================
  // PRODUCT IMAGE GALLERY
  // ========================================
  
  function initProductGallery() {
    const galleries = document.querySelectorAll('.product-gallery');
    
    galleries.forEach(gallery => {
      const mainImage = gallery.querySelector('.product-gallery__main img');
      const thumbnails = gallery.querySelectorAll('.product-gallery__thumbnail');
      
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', function(e) {
          e.preventDefault();
          
          // Update main image
          const newSrc = this.dataset.fullsize || this.querySelector('img')?.src;
          if (mainImage && newSrc) {
            mainImage.src = newSrc;
          }
          
          // Update active state
          thumbnails.forEach(t => t.classList.remove('active'));
          this.classList.add('active');
        });
      });
    });
  }

  // ========================================
  // INITIALIZE ALL FUNCTIONS
  // ========================================
  
  function init() {
    initSmoothScroll();
    initTestimonialSlider();
    initScrollAnimations();
    initProductVariantAccents();
    initQuantitySelector();
    initAddToCartFeedback();
    initStickyHeader();
    initLazyLoading();
    initProductGallery();
    
    console.log('Pup Spice custom scripts initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section updates (for theme editor)
  document.addEventListener('shopify:section:load', init);

})();
